import React, { useState, useEffect } from 'react';
import { Modal, Select, Skeleton, TextField } from '@gerdau/hefesto';
import { Stepper } from '@gerdau/hefesto-mais-web';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { useDispatch } from 'react-redux';
import useDebounce from '../../../../../../hooks/useDebounce';
import { FabricationPurchaseOrderService } from '../../../../../../services/FabricationPurchaseOrder';
import { IFabricationOrderExtraItem } from '../../../../../../store/ducks/fabrication-purchase-order/types';
import { loadSteelTypeDiameters } from '../../../../../../store/ducks/fabrication/actions';
import {
  IDiameter,
  IPieceType,
  ISteelType,
  ISteelTypesState
} from '../../../../../../store/ducks/fabrication/types';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../../utils/Tagging';
import { PieceTypesCard } from '../releaseToggleStyles';
import {
  Container,
  PieceTypesInputContainer,
  MeasureBlock,
  PieceTypesModalBlock,
  StepperLabel
} from '../PieceTypesModalContent/styles';

interface IPieceTypesModalContentProps {
  extraItem: IFabricationOrderExtraItem;
  setExtraItem(extraItem: IFabricationOrderExtraItem): void;
  selectedPieceType: IPieceType;
  selectedPieceTypeImage: string;
  steelTypesState?: ISteelTypesState;
  onCloseModal: () => void;
  onAddItemClick: () => void;
  canShowModal: boolean;
}

const maxMeasureValue = 1200;

const PieceTypesModal: React.FC<IPieceTypesModalContentProps> = ({
  extraItem,
  setExtraItem,
  selectedPieceType,
  selectedPieceTypeImage,
  steelTypesState,
  onCloseModal,
  onAddItemClick,
  canShowModal
}) => {
  const dispatch = useDispatch();

  const [selectedSteelType, setSelectedSteelType] = useState<ISteelType>();

  const onSelectSteelType = (steelTypeId: number) => {
    const selected = steelTypesState?.steelTypes.find(
      (st) => st.id === steelTypeId
    ) as ISteelType;
    setSelectedSteelType(selected);
    setExtraItem({
      ...extraItem,
      steelTypeId: selected.id,
      diameterId: undefined
    });

    if (!selected.diametersState) {
      dispatch(loadSteelTypeDiameters(steelTypeId));
    }
  };

  const updateExtraItemField = (field: string, newValue: number): void => {
    const updatedExtraItem = {
      ...extraItem,
      [field]: newValue
    };

    setExtraItem(updatedExtraItem);
    debouncedChange(updatedExtraItem);
  };

  const calculateExtraItemWeight = (
    updatedExtraItem: IFabricationOrderExtraItem
  ) => {
    const { pieceTypeId, amount, measureA, measureB, measureC } =
      updatedExtraItem;
    const selectedDiameter = selectedSteelType?.diametersState?.diameters.find(
      (d) => d.id === updatedExtraItem.diameterId
    ) as IDiameter;

    const cleanWeight = () => {
      setExtraItem({
        ...updatedExtraItem,
        weight: undefined
      });
    };

    const isInvaliMeasureA =
      selectedPieceType?.measureA &&
      updatedExtraItem.measureA !== undefined &&
      updatedExtraItem.measureA > maxMeasureValue;
    const isInvalidMeasureB =
      selectedPieceType?.measureB &&
      updatedExtraItem.measureB !== undefined &&
      updatedExtraItem.measureB > maxMeasureValue;
    const isInvalidMeasureC =
      selectedPieceType?.measureC &&
      updatedExtraItem.measureC !== undefined &&
      updatedExtraItem.measureC > maxMeasureValue;

    if (
      !pieceTypeId ||
      !amount ||
      !selectedDiameter ||
      !measureA ||
      (selectedPieceType.measureB && !measureB) ||
      (selectedPieceType.measureC && !measureC) ||
      isInvaliMeasureA ||
      isInvalidMeasureB ||
      isInvalidMeasureC
    ) {
      cleanWeight();
      return;
    }

    const weight = FabricationPurchaseOrderService.calculateExtraItemWeight(
      pieceTypeId,
      Number(amount),
      selectedDiameter,
      Number(measureA || 0),
      measureB ? Number(measureB) : 0,
      measureC ? Number(measureC) : 0
    );

    setTimeout(() => {
      setExtraItem({
        ...updatedExtraItem,
        weight
      });
    }, 100);
  };

  const debouncedChange = useDebounce(calculateExtraItemWeight, 500);

  const { screenName, itemDescription } = getTaggingInformationScreen(
    'modal_extra_itens_add_item'
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        event: 'page_view',
        screenName: 'LG:Comprar:CorteEDobra:ItensExtras:ModalAdicionarItem',
        page: document.location.pathname
      })
    );
  }, []);

  return (
    <>
      {canShowModal && (
        <Modal
          title="Adicionar item extra"
          buttons={{
            left: {
              name: 'Cancelar',
              children: 'Cancelar',
              onClick: () => {
                onCloseModal();
                dispatch(
                  sendTagging({
                    screenName,
                    itemDescription: `${itemDescription}cancelar`,
                    itemName: 'button:cancelar',
                    event: 'click'
                  })
                );
              }
            },
            right: {
              name: 'Adicionar item extra',
              children: 'Adicionar item extra',
              onClick: () => {
                onAddItemClick();
                dispatch(
                  sendTagging({
                    screenName,
                    itemDescription: `${itemDescription}adicionarItemExtra`,
                    itemName: 'button:adicionar_item_extra',
                    event: 'click'
                  })
                );
              },
              disabled:
                !extraItem.steelTypeId ||
                !extraItem.diameterId ||
                !extraItem.measureA ||
                !extraItem.amount ||
                (!!extraItem.amount && extraItem.amount < 0) ||
                (!!extraItem.measureA && extraItem.measureA < 0) ||
                (!!extraItem.measureB && extraItem.measureB < 0) ||
                (!!extraItem.measureC && extraItem.measureC < 0) ||
                extraItem.weight === undefined ||
                (selectedPieceType?.measureB && !extraItem.measureB) ||
                (selectedPieceType?.measureC && !extraItem.measureC)
            }
          }}
          onClose={() => onCloseModal()}
        >
          <Container>
            <header>
              Preencha os campos abaixo para adicionar um novo item
            </header>

            <div className="modal-content">
              <PieceTypesModalBlock>
                <PieceTypesInputContainer>
                  <Select
                    label={'Tipo da peça'}
                    name={'pieceTypes'}
                    value={selectedPieceType.description}
                    disabled={true}
                    options={[
                      {
                        label: selectedPieceType.description,
                        value: selectedPieceType.description
                      }
                    ]}
                  ></Select>
                </PieceTypesInputContainer>

                <PieceTypesInputContainer>
                  <StepperLabel>Quantidade</StepperLabel>
                  <Stepper
                    data-testid="quantityStepper"
                    label={''}
                    value={extraItem.amount}
                    name={'quantity'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      updateExtraItemField('amount', Number(e.target.value));
                    }}
                    disabled={!selectedPieceType.measureA}
                    minLength={0}
                  />
                </PieceTypesInputContainer>

                <PieceTypesInputContainer>
                  {steelTypesState &&
                    !steelTypesState.loading &&
                    !steelTypesState.error && (
                      <Select
                        label={'Tipo de aço'}
                        name={'steelType'}
                        placeholder={'Selecione um aço'}
                        value={selectedSteelType?.id?.toString() || undefined}
                        onSelect={(value) => onSelectSteelType(Number(value))}
                        options={
                          steelTypesState
                            ? steelTypesState.steelTypes.map((steelType) => {
                                return {
                                  label: steelType.description,
                                  value: steelType.id
                                } as unknown as TSelectOptionProps;
                              })
                            : []
                        }
                      />
                    )}

                  {(!steelTypesState ||
                    (steelTypesState && steelTypesState.loading)) && (
                    <Skeleton type="input" />
                  )}
                </PieceTypesInputContainer>

                <PieceTypesInputContainer>
                  {steelTypesState &&
                    !steelTypesState.loading &&
                    !steelTypesState.error && (
                      <Select
                        label={'Bitola'}
                        placeholder={'Em mm'}
                        value={
                          selectedSteelType?.diametersState?.loading
                            ? undefined
                            : extraItem.diameterId?.toString()
                        }
                        onSelect={(value) =>
                          updateExtraItemField('diameterId', Number(value))
                        }
                        disabled={
                          !selectedSteelType ||
                          (selectedSteelType &&
                            selectedSteelType.diametersState &&
                            selectedSteelType.diametersState.loading)
                        }
                        name={'diameter'}
                        options={
                          selectedSteelType && selectedSteelType.diametersState
                            ? selectedSteelType.diametersState.diameters.map(
                                (diameter) => {
                                  return {
                                    label: diameter.size.toString(),
                                    value: diameter.id.toString()
                                  };
                                }
                              )
                            : []
                        }
                      />
                    )}
                </PieceTypesInputContainer>
              </PieceTypesModalBlock>

              <PieceTypesModalBlock className="right-block">
                <MeasureBlock>
                  <div className="image-block">
                    <PieceTypesCard
                      className="inside-modal"
                      style={{
                        backgroundImage: `url(${selectedPieceTypeImage})`
                      }}
                    ></PieceTypesCard>
                  </div>

                  <PieceTypesModalBlock>
                    <PieceTypesInputContainer>
                      <StepperLabel>Medida a</StepperLabel>
                      <Stepper
                        label={''}
                        value={extraItem.measureA}
                        name={'measureA'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          updateExtraItemField(
                            'measureA',
                            Number(e.target.value)
                          );
                        }}
                        disabled={!selectedPieceType.measureA}
                        minLength={0}
                        maxLength={1200}
                      />
                    </PieceTypesInputContainer>

                    <PieceTypesInputContainer>
                      <StepperLabel>Medida b</StepperLabel>
                      <Stepper
                        label={''}
                        value={extraItem.measureB}
                        name={'measureB'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateExtraItemField(
                            'measureB',
                            Number(e.target.value)
                          )
                        }
                        disabled={!selectedPieceType.measureB}
                        minLength={0}
                        maxLength={1200}
                      />
                    </PieceTypesInputContainer>

                    <PieceTypesInputContainer>
                      <StepperLabel>Medida c</StepperLabel>
                      <Stepper
                        label={''}
                        value={extraItem.measureC}
                        name={'measureC'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateExtraItemField(
                            'measureC',
                            Number(e.target.value)
                          )
                        }
                        disabled={!selectedPieceType.measureC}
                        minLength={0}
                        maxLength={1200}
                      />
                    </PieceTypesInputContainer>
                  </PieceTypesModalBlock>
                </MeasureBlock>
                <PieceTypesInputContainer className="large-input">
                  <TextField
                    label={'Peso da peça'}
                    name={'pieceWeight'}
                    disabled={true}
                    placeholder=" - "
                    value={extraItem.weight}
                  ></TextField>
                </PieceTypesInputContainer>
              </PieceTypesModalBlock>
            </div>
          </Container>
        </Modal>
      )}
    </>
  );
};

export default PieceTypesModal;
